import { signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../../../config/firebase';
import { apiFetch } from '../../../shared/services/apiClient';
import { AuthErrorCode, resolverCodigoError } from '../domain/authErrors';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export async function login(): Promise<void> {
  await signInWithPopup(auth, googleProvider);
}

export async function logout(): Promise<void> {
  await signOut(auth);
}

/**
 * SPEC-185 — resultado del flujo de login, alineado con `authStatus` del handoff.
 * `cancelado` existe porque cerrar el popup NO es un error (RN-004 / CA-006).
 */
export type ResultadoLogin =
  | { estado: 'exito' }
  | { estado: 'cancelado' }
  | { estado: 'error'; codigo: AuthErrorCode };

// Códigos del SDK de Firebase que significan "el usuario desistió", no "falló algo".
const CANCELACIONES_DE_POPUP = [
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
];

function esCancelacion(error: unknown): boolean {
  const codigo = (error as { code?: string } | null)?.code;
  return typeof codigo === 'string' && CANCELACIONES_DE_POPUP.includes(codigo);
}

/**
 * SPEC-185 — UC-001..UC-006. Autentica con Google y verifica la AUTORIZACIÓN contra el
 * backend (design.md §4: no hay endpoint de login nuevo, se reutiliza el middleware que
 * protege /api/*, aquí a través de GET /api/auth/me).
 *
 * Si el backend rechaza, se cierra la sesión de Firebase: de lo contrario el usuario
 * quedaría "autenticado" en el cliente pero sin autorización, y el gate lo dejaría entrar
 * a una app cuyos requests fallarían todos (CA-004/CA-005: "no se crea sesión").
 */
export async function iniciarSesionConGoogle(): Promise<ResultadoLogin> {
  try {
    await signInWithPopup(auth, googleProvider);
  } catch (error) {
    if (esCancelacion(error)) {
      return { estado: 'cancelado' };
    }
    return { estado: 'error', codigo: 'NETWORK_ERROR' };
  }

  let response: Response;
  try {
    response = await apiFetch(`${API_URL}/auth/me`);
  } catch {
    // El OAuth de Google sí terminó, pero no se pudo hablar con el backend (CA-007).
    await signOut(auth).catch(() => undefined);
    return { estado: 'error', codigo: 'NETWORK_ERROR' };
  }

  if (response.ok) {
    return { estado: 'exito' };
  }

  const codigo = await resolverCodigoError(response);
  await signOut(auth).catch(() => undefined);
  return { estado: 'error', codigo };
}
